import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Radio, Skeleton } from "antd";
import { ReactComponent as Checked } from "../../../assets/Icons/checkedSquareBox.svg";
import { ReactComponent as SearchIcon } from "../../../assets/Icons/SearchIcon.svg";
import "./AllocateModal.scss";
import CustomSelect from "../../../Reuseable/Select/Select";
import {
  fetchAgencyProducts,
  fetchAgencyUsers,
  fetchRegions,
} from "../../../store/agencySlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchBankDashboard } from "../../../store/portfolioSlice";
import { ChangeCaseToUnallocate, postAllocateCase } from "../../../assets/api/allocation";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";

const AllocateModal = ({ open, isAll = false, portfolio, onClose, selectedIds = [], setRefresh, activeTab }) => {
  const { token } = useSelector((state) => state.auth);
  const { regions_list, regions_loading } = useSelector(
    (state) => state.agency
  );
  const [loading, setLoading] = useState(false);
  const [allocation, setAllocation] = useState({
    allocationType: "manual",
    selectedRole: null,
    selectedUser: null,
    product: null,
    pos: null,
    language: null,
    region: null,
    bank: null,
    agingBucket: null,
    users: [],
  });
  console.log(activeTab, 'activeTab');
  const isAllocateAll = activeTab === 'Allocated'

  const dispatch = useDispatch();

  const { products_list, user_list, user_loading } = useSelector(
    (state) => state?.agency
  );
  const { bankDashboard } = useSelector((state) => state?.portfolio);

  const roleOptions = [
    { label: "Supervisor", value: "supervisors" },
    { label: "Field Agent", value: "field_agents" },
    { label: "Teamlead", value: "teamleads" },
    { label: "Telecaller", value: "telecallers" },
  ];

  const rolesVal = {
    supervisors: "supervisor",
    field_agents: "fieldagent",
    teamleads: "teamlead",
    telecallers: "telecaller",
  };

  const roleIdKeys = {
    supervisors: "supervisor_id",
    field_agents: "fieldagent_id",
    teamleads: "teamlead_id",
    telecallers: "telecaller_id",
  };

  const options = useMemo(() => {
    if (user_loading) {
      return Array(5)
        .fill(null)
        .map((_, index) => ({
          label: <Skeleton.Input size="small" active />,
          value: index,
        }));
    }
    const roleIdKey = roleIdKeys[allocation?.selectedRole] || "id";
    return (user_list?.results || [])?.map((u) => ({
      label: u?.full_name || u?.email,
      value: u?.[roleIdKey] ?? u?.id,
    }));
  }, [user_loading, user_list, allocation?.selectedRole]);

  const bankOptions =
    bankDashboard?.map((item) => ({
      label: item.name,
      value: item.id,
    })) || [];

  useEffect(() => {
    if (!open || allocation?.allocationType === 'manual' || isAllocateAll) {
      return;
    }
    dispatch(fetchAgencyProducts());
    dispatch(fetchBankDashboard());
    dispatch(fetchRegions());
  }, [dispatch, open, allocation?.allocationType]);

  useEffect(() => {
    if (!open) {
      // Reset on close
      setAllocation({
        allocationType: "manual",
        selectedRole: null,
        selectedUser: null,
        product: null,
        pos: null,
        language: null,
        region: null,
        bank: null,
        agingBucket: null,
      });
    }
  }, [open]);

  const updateAllocation = (field, value) => {
    setAllocation((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const cleanObject = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([, v]) =>
          v !== null &&
          v !== undefined &&
          v !== "" &&
          !(Array.isArray(v) && v.length === 0)
      )
    );
  };


  const handleSubmit = async () => {
    try {
      if (!selectedIds.length && !isAll) {
        return CustomNotification.show("No cases selected", "error");
      }

      setLoading(true);

      const {
        allocationType,
        selectedRole,
        users,
        product,
        language,
        region,
        bank,
        agingBucket,
      } = allocation;

      let payload = { allocation_type: allocationType };

      /* ============ AUTO ALLOCATION ============ */
      if (allocationType === "auto") {
        if (isAll) {
          // AUTO + ALL
          payload = {
            allocation_type: "auto",
            all: true,
            product,
            language,
            region,
            portfolio: bank,
            aging_bucket: agingBucket,
          };
        } else {
          // AUTO + SELECTED CASES
          payload = {
            allocation_type: "auto",
            cases: selectedIds,
            product,
            language,
            region,
            portfolio: bank,
            aging_bucket: agingBucket,
          };
        }
      }

      /* ============ MANUAL ALLOCATION ============ */
      if (allocationType === "manual") {
        if (!selectedRole || !users?.length) {
          setLoading(false);
          return CustomNotification.show(
            "Please select a role and at least one user",
            "error"
          );
        }

        if (isAll) {
          // MANUAL + ALL
          payload = {
            allocation_type: "manual",
            all: true,
            portfolio: portfolio,
            [selectedRole]: users,
          };
        } else {
          // MANUAL + SELECTED CASES
          payload = {
            allocation_type: "manual",
            portfolio: portfolio,
            cases: selectedIds,
            [selectedRole]: users,
          };
        }
      }
      payload = cleanObject(payload);
      /* ============ API CALL ============ */
      const response = await postAllocateCase(
        { role_type: selectedRole, payload },
        token
      );

      if (response?.status === 200) {
        CustomNotification.show(
          response?.data?.message || "Cases allocated successfully",
          "success"
        );

        setAllocation({ allocationType });
        onClose();
        setRefresh((prev) => !prev);

        const downloadUrl = response?.data?.data?.csv_files?.csv_download_url;
        if (downloadUrl && selectedRole === "telecallers") {
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.download = "";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        const errorMsg =
          response?.response?.data?.errors ||
          "Something went wrong during allocation";
        CustomNotification.show(errorMsg, "error");
      }
    } catch (error) {
      CustomNotification.show("Unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAllocation = async () => {
    try {
      setLoading(true);
      const res = await ChangeCaseToUnallocate(
        {
          all: true,
          portfolio: portfolio,
        },
        token,
      );
      if (res?.status === 200) {
        CustomNotification.show(res?.data?.message, "success");
        onClose();
        setRefresh((prev) => !prev);
        setLoading(false);
      }
    } catch (error) {
      console.error(error, "error message;");
    }
  };


  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Allocate Case"
      width={638}
      className="allocateCaseModal"
    >
      {/* Selected IDs */}
      {
        !isAll && (
          <div className="selectedIdsContainer">
            <p className="label">IDs selected</p>
            <div className="idGrid" style={{}}>
              {selectedIds?.map((id, index) => (
                <div key={index} className="idCheckbox">
                  <Checked className="checkedIcon" />
                  <span>{id}</span>
                </div>
              ))}
            </div>
          </div>
        )
      }

      {/* Role */}
      <div className="formGroup">
        <CustomSelect
          label="Select Role"
          placeholder="Select Role"
          options={roleOptions.map((r) => ({ label: r.label, value: r.value }))}
          value={allocation.selectedRole}
          onChange={(value) => {
            setAllocation((prev) => ({
              ...prev,
              selectedRole: value,
              users: [],
            }));
            const query = value ? { role: rolesVal?.[value] } : {};
            dispatch(fetchAgencyUsers(query));
          }}
        />
      </div>

      {/* Allocation Type */}
      <div className="formGroup">
        <p className="label">Select Allocation type</p>
        <Radio.Group
          value={allocation.allocationType}
          onChange={(e) => {
            updateAllocation("allocationType", e.target.value);
          }}
        >
          <Radio value="manual">Manual Allocation</Radio>
          {/* {!isAll && ( */}
          <Radio value="auto">Auto Allocation</Radio>
          {isAllocateAll && <Radio value="unallocate">Unallocate</Radio>}
          {/* ) } */}
        </Radio.Group>
      </div>

      {/* Manual Allocation */}
      {allocation.allocationType === "manual" && (
        <div className="selectUserWrapper">
          <p>Select User</p>
          <CustomSelect
            mode="multiple"
            placeholder="Search & Select Users"
            value={allocation.users}
            onChange={(value) => updateAllocation("users", value)}
            options={options}
            showSearch
            filterOption={false} // disable local filter
            onInputKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // stop auto select
                e.stopPropagation(); // prevent form submission
                const val = e.target.value.trim();
                const query = {
                  role: rolesVal?.[allocation.selectedRole],
                  search: val || undefined,
                };
                dispatch(fetchAgencyUsers(query));
              }
            }}
            onSearchClick={(val) => {
              // if your CustomSelect supports explicit search button
              const query = {
                role: rolesVal?.[allocation.selectedRole],
                search: val || undefined,
              };
              dispatch(fetchAgencyUsers(query));
            }}
          />
        </div>
      )}

      {/* Auto Allocation Filters */}
      {allocation.allocationType === "auto" && (
        <div className="autoAllocationGrid">
          <CustomSelect
            label="Product"
            placeholder="Select Product"
            options={products_list?.results?.map((product) => ({
              label: product?.products,
              value: product?.id,
            }))}
            value={allocation.product}
            onChange={(value) => updateAllocation("product", value)}
          />

          <CustomSelect
            label="Language"
            placeholder="Select Language"
            options={[
              { label: "Hindi", value: "Hindi" },
              { label: "English", value: "English" },
              { label: "Marathi", value: "Marathi" },
            ]}
            value={allocation.language}
            onChange={(value) => updateAllocation("language", value)}
          />

          <CustomSelect
            label="Region"
            placeholder="Select Region"
            options={regions_list?.map((region) => ({
              label: region?.name,
              value: region?.id,
            }))}
            value={allocation.region}
            onChange={(value) => updateAllocation("region", value)}
          />

          <CustomSelect
            label="Bank"
            placeholder="Select Bank"
            options={bankOptions}
            value={allocation.bank}
            onChange={(value) => updateAllocation("bank", value)}
          />

          <CustomSelect
            label="Aging Bucket"
            placeholder="Select Aging Bucket"
            options={[
              { label: "1", value: "1" },
              { label: "2", value: "2" },
              { label: "3", value: "3" },
              { label: "4", value: "4" },
              { label: "5", value: "5" },
              { label: "6", value: "6" },
              { label: "7", value: "7" },
              { label: "8", value: "8" },
            ]}
            value={allocation.agingBucket}
            onChange={(value) => updateAllocation("agingBucket", value)}
          />
        </div>
      )}

      {/* Submit */}
      <div className="submitBtnWrapper">
        <Button
          type="primary"
          className="submitBtn"
          onClick={isAllocateAll ? handleUpdateAllocation : handleSubmit}
          loading={loading}
        >
          {isAllocateAll ? "Unallocate Cases" : "Allocate Cases"}
        </Button>
      </div>
    </Modal>
  );
};

export default AllocateModal;
